import { FC, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useEntityBasePath } from "../../../../../../utils/entityPaths";
import {
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Autocomplete
} from "@mui/material";
import { CommentDto, TradingCounterParty } from "../../../../../../api/Api";
import {
  useAssignees,
  useCounterpartyProgressOverride,
  useUpdateCounterparty
} from "../../../../dataHooks/counterpartyQueries";
import { createLifecycleActions } from "../../../../../../components/lifecycle/createLifecycleActions";
import { CommentsSection } from "../../sharedComponents/commentsSection";
import { LifecycleDialog } from "../../sharedComponents/lifecycleDialog";
import { SignOffNoteField } from "../../../../../../components/lifecycle/signOffNoteField";
import { useLifecycleComments } from "../../../../../../components/lifecycle/useLifecycleComments";
import { useRoles } from "../../../../../../auth/useRoles";
import { ownsLifecycle } from "../../../../../../auth/permissions";

type Props = {
  specificCounterparty: TradingCounterParty;
};

type FormData = {
  agreementReview: string;
  agreementReviewAssignee: string;
  counterpartySetup: string;
  counterpartySetupAssignee: string;
  testTrade: string;
  testTradeAssignee: string;
  iban: string;
  ibanAssignee: string;
  callback: string;
  callbackAssignee: string;
  tradeConfirmationSetup: string;
  comments: CommentDto[];
  signOffNote: string;
};

export const Finance: FC<Props> = ({ specificCounterparty }) => {
  const params = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const basePath = useEntityBasePath("counterparty");
  const roles = useRoles();
  const progressOverride = useCounterpartyProgressOverride(
    specificCounterparty,
    "finance"
  );

  const financeAssignees = useAssignees("finance");

  const [formData, setFormData] = useState<FormData>({
    agreementReview:
      specificCounterparty?.financeLifecycle?.agreementReview || "",
    agreementReviewAssignee:
      specificCounterparty?.financeLifecycle?.agreementReviewAssignee || "",
    counterpartySetup:
      specificCounterparty?.financeLifecycle?.counterpartySetup || "",
    counterpartySetupAssignee:
      specificCounterparty?.financeLifecycle?.counterpartySetupAssignee || "",
    testTrade: specificCounterparty?.financeLifecycle?.testTrade || "",
    testTradeAssignee:
      specificCounterparty?.financeLifecycle?.testTradeAssignee || "",
    iban: specificCounterparty?.financeLifecycle?.iban || "",
    ibanAssignee: specificCounterparty?.financeLifecycle?.ibanAssignee || "",
    callback: specificCounterparty?.financeLifecycle?.callback || "",
    callbackAssignee:
      specificCounterparty?.financeLifecycle?.callbackAssignee || "",
    tradeConfirmationSetup:
      specificCounterparty?.financeLifecycle?.tradeConfirmationSetup || "",
    comments: specificCounterparty?.financeLifecycle?.comments || [],
    signOffNote: specificCounterparty?.financeLifecycle?.signOffNote || ""
  });

  const handleClose = () => {
    navigate(`${basePath}/${params.id}`);
  };

  const updateCounterparty = useUpdateCounterparty({
    onSuccess: handleClose
  });
  const commentUpdate = useUpdateCounterparty();

  const buildCounterparty = (
    values: FormData & { complete?: boolean }
  ): TradingCounterParty => ({
    ...specificCounterparty,
    financeLifecycle: {
      ...specificCounterparty.financeLifecycle,
      ...values
    }
  });

  const {
    currentComment,
    setCurrentComment,
    addComment,
    deleteComment,
    isSaving
  } = useLifecycleComments({
    formData,
    setFormData,
    update: commentUpdate,
    buildEntityWithComments: (comments) =>
      buildCounterparty({ ...formData, comments })
  });

  const handleChange = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const { handleSave, handleComplete } = createLifecycleActions({
    formData,
    currentComment,
    currentProgress: specificCounterparty.progress?.finance,
    canSetInProgress: ownsLifecycle(roles, "finance"),
    buildEntity: (values, progress) => {
      const saved = buildCounterparty(values);
      return {
        ...saved,
        progress: { ...saved.progress, finance: progress }
      };
    },
    mutate: updateCounterparty.mutate
  });

  const open = location.pathname.includes("finance");

  const canComplete =
    formData.agreementReview &&
    formData.counterpartySetup &&
    formData.testTrade &&
    formData.callback;

  return (
    <LifecycleDialog
      open={open}
      onClose={handleClose}
      title="Finance"
      progressOverride={progressOverride}
      maxWidth="md"
      actions={{
        onClose: handleClose,
        onSave: handleSave,
        onComplete: handleComplete,
        canComplete: !!canComplete,
        isSaving: updateCounterparty.isPending
      }}
    >
      <Box sx={{ paddingTop: 2 }} gap={2} display="flex" flexDirection="column">
        <Box display="flex" gap={2}>
          <FormControl size="medium" required sx={{ flex: 1 }}>
            <InputLabel>
              Agreement review (invoicing and settlement terms)
            </InputLabel>
            <Select
              label="Agreement review (invoicing and settlement terms)"
              value={formData.agreementReview}
              onChange={(e) => handleChange("agreementReview", e.target.value)}
            >
              <MenuItem value="approved">
                <Typography variant="body1" component="span">
                  Approved
                </Typography>
              </MenuItem>
              <MenuItem value="rejected">
                <Typography variant="body1" component="span">
                  Rejected
                </Typography>
              </MenuItem>
            </Select>
          </FormControl>

          <Autocomplete
            sx={{ flex: 1 }}
            options={financeAssignees.data || []}
            getOptionLabel={(option) => `${option.name} (${option.email})`}
            value={
              financeAssignees.data?.find(
                (a) => a.email === formData.agreementReviewAssignee
              ) || null
            }
            onChange={(_, newValue) =>
              handleChange("agreementReviewAssignee", newValue?.email || "")
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Agreement review assignee"
                size="medium"
              />
            )}
          />
        </Box>

        <Box display="flex" gap={2}>
          <FormControl size="medium" required sx={{ flex: 1 }}>
            <InputLabel>Counterparty setup in Business Central</InputLabel>
            <Select
              label="Counterparty setup in Business Central"
              value={formData.counterpartySetup}
              onChange={(e) =>
                handleChange("counterpartySetup", e.target.value)
              }
            >
              <MenuItem value="initiated">
                <Typography variant="body1" component="span">
                  Initiated
                </Typography>
              </MenuItem>
              <MenuItem value="completed">
                <Typography variant="body1" component="span">
                  Completed
                </Typography>
              </MenuItem>
              <MenuItem value="rejected">
                <Typography variant="body1" component="span">
                  Rejected
                </Typography>
              </MenuItem>
            </Select>
          </FormControl>

          <Autocomplete
            sx={{ flex: 1 }}
            options={financeAssignees.data || []}
            getOptionLabel={(option) => `${option.name} (${option.email})`}
            value={
              financeAssignees.data?.find(
                (a) => a.email === formData.counterpartySetupAssignee
              ) || null
            }
            onChange={(_, newValue) =>
              handleChange("counterpartySetupAssignee", newValue?.email || "")
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Counterparty setup assignee"
                size="medium"
              />
            )}
          />
        </Box>

        <Box display="flex" gap={2}>
          <FormControl required size="medium" sx={{ flex: 1 }}>
            <InputLabel>Test trade required for settlement method</InputLabel>
            <Select
              label="Test trade required for settlement method"
              value={formData.testTrade}
              onChange={(e) => handleChange("testTrade", e.target.value)}
            >
              <MenuItem value="needed">
                <Typography variant="body1" component="span">
                  Needed
                </Typography>
              </MenuItem>
              <MenuItem value="not_needed">
                <Typography variant="body1" component="span">
                  Not needed
                </Typography>
              </MenuItem>
            </Select>
          </FormControl>

          <Autocomplete
            sx={{ flex: 1 }}
            options={financeAssignees.data || []}
            getOptionLabel={(option) => `${option.name} (${option.email})`}
            value={
              financeAssignees.data?.find(
                (a) => a.email === formData.testTradeAssignee
              ) || null
            }
            onChange={(_, newValue) =>
              handleChange("testTradeAssignee", newValue?.email || "")
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Test trade assignee"
                size="medium"
              />
            )}
          />
        </Box>

        <Box display="flex" gap={2}>
          <TextField
            sx={{ flex: 1 }}
            label="IBAN"
            size="medium"
            value={formData.iban}
            onChange={(e) => handleChange("iban", e.target.value)}
          />

          <Autocomplete
            sx={{ flex: 1 }}
            options={financeAssignees.data || []}
            getOptionLabel={(option) => `${option.name} (${option.email})`}
            value={
              financeAssignees.data?.find(
                (a) => a.email === formData.ibanAssignee
              ) || null
            }
            onChange={(_, newValue) =>
              handleChange("ibanAssignee", newValue?.email || "")
            }
            renderInput={(params) => (
              <TextField {...params} label="IBAN assignee" size="medium" />
            )}
          />
        </Box>

        <Box display="flex" gap={2}>
          <FormControl required size="medium" sx={{ flex: 1 }}>
            <InputLabel>Callback</InputLabel>
            <Select
              label="Callback"
              value={formData.callback}
              onChange={(e) => handleChange("callback", e.target.value)}
            >
              <MenuItem value="initiated">
                <Typography variant="body1" component="span">
                  Initiated
                </Typography>
              </MenuItem>
              <MenuItem value="completed">
                <Typography variant="body1" component="span">
                  Completed
                </Typography>
              </MenuItem>
              <MenuItem value="not_needed">
                <Typography variant="body1" component="span">
                  Not needed
                </Typography>
              </MenuItem>
            </Select>
          </FormControl>

          <Autocomplete
            sx={{ flex: 1 }}
            options={financeAssignees.data || []}
            getOptionLabel={(option) => `${option.name} (${option.email})`}
            value={
              financeAssignees.data?.find(
                (a) => a.email === formData.callbackAssignee
              ) || null
            }
            onChange={(_, newValue) =>
              handleChange("callbackAssignee", newValue?.email || "")
            }
            renderInput={(params) => (
              <TextField {...params} label="Callback assignee" size="medium" />
            )}
          />
        </Box>

        {specificCounterparty.marketAccessLifecycle?.tradeConfirmation ===
          "needed" && (
          <FormControl required size="medium">
            <InputLabel>Trade Confirmation Setup</InputLabel>
            <Select
              label="Trade Confirmation Setup"
              value={formData.tradeConfirmationSetup}
              onChange={(e) =>
                handleChange("tradeConfirmationSetup", e.target.value)
              }
            >
              <MenuItem value="completed">
                <Typography variant="body1" component="span">
                  Completed
                </Typography>
              </MenuItem>
              <MenuItem value="not_completed">
                <Typography variant="body1" component="span">
                  Not completed
                </Typography>
              </MenuItem>
            </Select>
          </FormControl>
        )}

        <SignOffNoteField
          value={formData.signOffNote}
          onChange={(value) => handleChange("signOffNote", value)}
        />

        <CommentsSection
          loading={isSaving}
          comments={formData.comments}
          currentComment={currentComment}
          onCurrentCommentChange={setCurrentComment}
          saveComment={addComment}
          onDeleteComment={deleteComment}
        />
      </Box>
    </LifecycleDialog>
  );
};
